import { track } from '../track';

/**
 * The single sitewide auth event name (HB-984). One event with an `action`
 * property, rather than a name per action, so the pipeline can read the whole
 * auth funnel — including failures — off one stream.
 */
export const AUTH_EVENT = 'AUTH/EVENT';

export enum AuthAction {
	Login = 'login',
	Logout = 'logout',
	AccountCreated = 'account_created',
	LoginFailed = 'login_failed',
	/**
	 * Not a session: the visitor handed over an email that resolves their device
	 * to a person (checkout, newsletter, forms…). Kept distinct from `login` so
	 * real sessions stay countable, while commerce stops being invisible.
	 */
	Identified = 'identified'
}

/**
 * How the user authenticated. Commerce surfaces have no credentials, so they
 * report the email capture itself.
 */
export enum AuthMethod {
	Password = 'password',
	Email = 'email'
}

/**
 * Non-error `reason` values: what prompted this transition. Every `login` and
 * `logout` carries one, so a deliberate sign-in or sign-out is never counted
 * alongside one the app performed on the user's behalf. Failed logins pass the
 * provider's own error code instead.
 */
export enum AuthReason {
	/** The user typed their credentials into the login form. */
	Credentials = 'credentials',
	/** Signed straight in off the back of confirming a new account. */
	AccountCreated = 'account_created',
	/** Signed straight in off the back of completing a password reset. */
	PasswordReset = 'password_reset',
	/** The user chose to log out. */
	UserInitiated = 'user_initiated',
	/** The app signed the user out after a request failed, not the user. */
	SessionError = 'session_error'
}

/**
 * Where in the site the event originated. Names the surface, not the route, so
 * the same journey stays comparable when URLs change.
 */
export enum AuthProvenance {
	News = 'news',
	Checkout = 'checkout',
	ResumeCheckout = 'resume_checkout',
	Apply = 'apply',
	Newsletter = 'newsletter',
	NewsletterWidget = 'newsletter_widget',
	Forms = 'forms',
	ContactUs = 'contact_us'
}

type AuthEventProperties = {
	action: AuthAction;
	method: AuthMethod;
	provenance: AuthProvenance;
	/**
	 * Cognito error code for `login_failed`. Deliberately narrow: never pass the
	 * submitted password or any other credential.
	 */
	reason?: string;
	/**
	 * Which block on the page fired this, for surfaces that render more than one
	 * at the same URL — a drop page shows both an inline newsletter section and a
	 * countdown dialog, and `track` already stamps `url`, so only this tells them
	 * apart. Omit it where the URL alone identifies the surface. Kept separate
	 * from `provenance` so that stays a closed set the pipeline can group on.
	 */
	placement?: string;
};

/**
 * Fires the sitewide auth event (HB-984).
 *
 * Every sign-in is a stitching opportunity — the moment a device hash resolves
 * to a person — so this is called next to `identifyUser` on success paths, and
 * on its own for logout and failed logins (where there is no one to identify).
 */
export const trackAuthEvent = ({
	action,
	method,
	provenance,
	reason,
	placement
}: AuthEventProperties) => {
	track(AUTH_EVENT, {
		action,
		method,
		provenance,
		...(reason !== undefined ? { reason } : {}),
		...(placement !== undefined ? { placement } : {})
	});
};
